/**
 * Rep comparison table - the "spot underperformers fast" surface.
 *
 * A table rather than a chart on purpose: ~7+ classes that all carry meaning belong in
 * a table, and there are up to ~40 active reps. Rate cells carry a micro-bar with a
 * hairline median marker, so an outlier is visible without reading digits.
 *
 * Low-confidence handling matters here. A rep with 3 recorded doors and 1 sale shows a
 * 33% close rate that means nothing; ranking them against a rep with 400 doors is
 * misleading. Reps below MIN_DOORS_FOR_RANKING are flagged and sorted last.
 */

import { useMemo, useState } from "react";
import { TableCell, TableRow } from "flowbite-react";
import { HiExclamationCircle } from "react-icons/hi";
import { SortableTable } from "../SortableTable";
import type { SortState } from "../../lib/sorting";
import { updateSortState } from "../../lib/sorting";
import type { RepMetrics } from "../../services/sales-metrics";
import { formatCurrency, formatNumber, formatRate, median } from "./format";
import { MicroBarCell } from "./primitives";

/**
 * Minimum recorded doors before a rep's rates are treated as meaningful. Chosen so a
 * single lucky or unlucky door cannot swing a rate by more than a few points.
 */
export const MIN_DOORS_FOR_RANKING = 20;

/**
 * A rep whose close rate is ~100% is almost certainly only creating a customer record
 * when a deal closes, rather than logging every door. Their "close rate" then measures
 * their logging habit, not their selling. Verified on production: for Aug 2026 this was
 * 20 of 28 reps, covering 36% of all recorded doors.
 *
 * These reps are still shown - their revenue is real - but their funnel rates are
 * marked as not comparable, so nobody reads a 100% close rate as performance.
 */
export function isCloseRateArtifact(rep: RepMetrics): boolean {
  return rep.closeRate !== null && rep.closeRate >= 0.99;
}

type SortKey =
  | "fullName"
  | "recordedDoors"
  | "contractsSigned"
  | "revenue"
  | "revenuePerDoor"
  | "closeRate"
  | "contactRate"
  | "avgContractValue"
  | "goalAttainment";

const COLUMNS: { key: SortKey; label: string; sortable?: boolean; explanation?: string }[] = [
  {
    key: "fullName",
    label: "Vertriebler",
    sortable: true,
    explanation:
      "Name des Vertriebsmitarbeiters. Warnsymbole weisen auf geringe Aktivität (< 20 Türen) oder unvollständiges Funnel-Tracking hin.",
  },
  {
    key: "recordedDoors",
    label: "Erfasste Türen",
    sortable: true,
    explanation:
      "Anzahl der vom Vertriebler in der App dokumentierten Haustüren. Bildet die Berechnungsgrundlage für alle Quoten.",
  },
  {
    key: "contactRate",
    label: "Kontaktrate",
    sortable: true,
    explanation:
      "Anteil der erfassten Türen, bei denen ein persönlicher Kontakt mit Bewohnern zustande kam (Kontaktiert / Erfasste Türen).",
  },
  {
    key: "closeRate",
    label: "Abschlussrate",
    sortable: true,
    explanation:
      "Anteil der erfassten Türen, die zu mindestens einem Vertragsabschluss führten (Kunden mit Abschluss / Erfasste Türen).",
  },
  {
    key: "contractsSigned",
    label: "Verträge",
    sortable: true,
    explanation:
      "Gesamtzahl der vom Vertriebler im gewählten Zeitraum unterzeichneten Verträge (inkl. Mehrfachabschlüsse pro Kunde).",
  },
  {
    key: "revenue",
    label: "Umsatz",
    sortable: true,
    explanation:
      "Gesamter erzielter Provisionsumsatz aller unterschriebenen Verträge des Vertrieblers im gewählten Zeitraum.",
  },
  {
    key: "revenuePerDoor",
    label: "Umsatz / Tür",
    sortable: true,
    explanation:
      "Durchschnittlich erzielter Umsatz pro erfasster Haustür (Umsatz / Erfasste Türen). Zentrale Kennzahl für Zeiteffizienz im Feld.",
  },
  {
    key: "avgContractValue",
    label: "Ø Vertragswert",
    sortable: true,
    explanation:
      "Durchschnittlicher Provisionswert pro abgeschlossenem Vertrag des Vertrieblers (Umsatz / Verträge).",
  },
  {
    key: "goalAttainment",
    label: "Zielerreichung",
    sortable: true,
    explanation:
      "Prozentuale Zielerreichung bezogen auf das im System hinterlegte monatliche Umsatzziel des Vertrieblers.",
  },
];

export function RepTable({ reps }: { reps: RepMetrics[] }) {
  // Default sort: revenue per door. Ranking on raw revenue just ranks who worked most.
  const [sortState, setSortState] = useState<SortState<SortKey>>({
    key: "revenuePerDoor",
    direction: "desc",
  });

  const benchmarks = useMemo(() => {
    // Exclude close-rate artifacts: including reps who only log closes would drag the
    // median toward 100% and make genuine full-funnel loggers look broken.
    const ranked = reps.filter(
      (r) => r.recordedDoors >= MIN_DOORS_FOR_RANKING && !isCloseRateArtifact(r)
    );
    return {
      closeRate: median(ranked.map((r) => r.closeRate)),
      contactRate: median(ranked.map((r) => r.contactRate)),
      revenuePerDoor: median(ranked.map((r) => r.revenuePerDoor)),
    };
  }, [reps]);

  const maxRevenuePerDoor = useMemo(
    () => Math.max(...reps.map((r) => r.revenuePerDoor ?? 0), 1),
    [reps]
  );

  const sorted = useMemo(() => {
    const dir = sortState.direction === "asc" ? 1 : -1;
    return [...reps].sort((a, b) => {
      // Low-confidence reps always sort last, regardless of direction: their rates
      // are noise and would otherwise top a rate-sorted list.
      const aLow = a.recordedDoors < MIN_DOORS_FOR_RANKING;
      const bLow = b.recordedDoors < MIN_DOORS_FOR_RANKING;
      if (aLow !== bLow) return aLow ? 1 : -1;

      const av = a[sortState.key];
      const bv = b[sortState.key];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return av.localeCompare(bv) * dir;
      }
      return ((av as number) - (bv as number)) * dir;
    });
  }, [reps, sortState]);

  if (reps.length === 0) {
    return (
      <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
        Keine Vertriebler mit Aktivität im gewählten Zeitraum.
      </p>
    );
  }

  const lowConfidenceCount = reps.filter(
    (r) => r.recordedDoors < MIN_DOORS_FOR_RANKING
  ).length;
  const artifactCount = reps.filter(isCloseRateArtifact).length;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="viz-scroll-x">
        <SortableTable
          columns={COLUMNS}
          sortState={sortState}
          onSort={(key) => setSortState((s) => updateSortState(s, key as SortKey))}
          flush
        >
            {sorted.map((rep) => {
              const lowConfidence = rep.recordedDoors < MIN_DOORS_FOR_RANKING;
              const artifact = isCloseRateArtifact(rep);
              return (
                <TableRow key={rep.workerId} className={lowConfidence ? "opacity-60" : ""}>
                  <TableCell className="whitespace-nowrap p-3 text-sm font-medium">
                    <span className="flex items-center gap-1.5">
                      {rep.fullName}
                      {lowConfidence ? (
                        /* Status never rests on color alone: icon + title text. */
                        <HiExclamationCircle
                          style={{ color: "var(--viz-status-warning)" }}
                          title={`Weniger als ${MIN_DOORS_FOR_RANKING} erfasste Türen – Raten wenig aussagekräftig`}
                          aria-label="Wenig Daten"
                        />
                      ) : null}
                      {artifact ? (
                        <HiExclamationCircle
                          style={{ color: "var(--viz-status-serious)" }}
                          title="Erfasst offenbar nur Abschlüsse, nicht jede Tür – Funnel-Raten nicht vergleichbar"
                          aria-label="Nur Abschlüsse erfasst"
                        />
                      ) : null}
                    </span>
                  </TableCell>

                  <TableCell className="viz-tabular whitespace-nowrap p-3 text-sm">
                    {formatNumber(rep.recordedDoors)}
                  </TableCell>

                  <TableCell className="p-3">
                    <MicroBarCell rate={rep.contactRate} benchmark={benchmarks.contactRate} />
                  </TableCell>

                  <TableCell className="p-3">
                    {artifact ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        n.&nbsp;v.
                      </span>
                    ) : (
                      <MicroBarCell rate={rep.closeRate} benchmark={benchmarks.closeRate} />
                    )}
                  </TableCell>

                  <TableCell className="viz-tabular whitespace-nowrap p-3 text-sm">
                    {formatNumber(rep.contractsSigned)}
                  </TableCell>

                  <TableCell className="viz-tabular whitespace-nowrap p-3 text-sm font-medium">
                    {formatCurrency(rep.revenue)}
                  </TableCell>

                  <TableCell className="p-3">
                    <MicroBarCell
                      rate={rep.revenuePerDoor}
                      benchmark={benchmarks.revenuePerDoor}
                      max={maxRevenuePerDoor}
                    />
                  </TableCell>

                  <TableCell className="viz-tabular whitespace-nowrap p-3 text-sm">
                    {formatCurrency(rep.avgContractValue)}
                  </TableCell>

                  <TableCell className="viz-tabular whitespace-nowrap p-3 text-sm">
                    {rep.goalAttainment === null ? (
                      <span className="text-gray-500 dark:text-gray-400" title="Kein Monatsziel hinterlegt">
                        —
                      </span>
                    ) : (
                      formatRate(rep.goalAttainment, 0)
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
        </SortableTable>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Der Strich in den Balken markiert den Median aller Vertriebler mit mindestens{" "}
        {MIN_DOORS_FOR_RANKING} erfassten Türen.
        {lowConfidenceCount > 0
          ? ` ${lowConfidenceCount} Vertriebler mit zu wenig Daten werden nachrangig sortiert.`
          : ""}
        {artifactCount > 0
          ? ` ${artifactCount} Vertriebler erfassen offenbar nur Abschlüsse statt jeder Tür; ihre Funnel-Raten sind mit „n. v." markiert und fließen nicht in den Median ein.`
          : ""}
      </p>
    </div>
  );
}
